import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@ouropix/ui';
import {
  CheckCircle, AlertTriangle, Filter, RefreshCw,
} from 'lucide-react';
import { api } from '@/lib/api';
import { unwrapList } from '@/lib/api-helpers';
import { SecurityAlertCard, type SecurityAlert } from '@/components/admin-ops/SecurityAlertCard';

export const Route = createFileRoute('/_admin/alertas')({
  component: AlertasPage,
});

interface SecurityStats {
  total_alerts: number;
  unresolved_alerts: number;
  critical_alerts: number;
  blocked_ips_today: number;
  failed_logins_today: number;
}

function AlertasPage() {
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterResolved, setFilterResolved] = useState<string>('unresolved');

  const { data: stats } = useQuery<SecurityStats>({
    queryKey: ['security-stats'],
    queryFn: () => api.get('/fraud/alerts/stats').then((r) => r.data),
    retry: false,
    throwOnError: false,
  });

  const { data: alerts = [], isLoading, error, refetch } = useQuery<SecurityAlert[]>({
    queryKey: ['security-alerts', filterSeverity, filterResolved],
    queryFn: () =>
      api.get('/fraud/alerts', {
        params: {
          severity: filterSeverity !== 'all' ? filterSeverity : undefined,
          resolved: filterResolved === 'all' ? undefined : filterResolved === 'resolved' ? true : false,
          limit: 100,
        },
      }).then((r) => unwrapList<SecurityAlert>(r.data, 'alerts')),
    retry: 2,
    retryDelay: 1000,
  });

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Alertas de Segurança</h1>
          <p className="text-muted-foreground">Monitoramento de atividades suspeitas</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="bg-card/50">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Total (24h)</div>
            <div className="text-xl font-bold">{stats?.total_alerts ?? 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Nao resolvidos</div>
            <div className="text-xl font-bold text-yellow-400">{stats?.unresolved_alerts ?? 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Criticos</div>
            <div className="text-xl font-bold text-red-400">{stats?.critical_alerts ?? 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">IPs bloqueados</div>
            <div className="text-xl font-bold">{stats?.blocked_ips_today ?? 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-card/50">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Logins falhos</div>
            <div className="text-xl font-bold">{stats?.failed_logins_today ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card/50">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filtros:</span>
            </div>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-full sm:w-[140px] h-8">
                <SelectValue placeholder="Severidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="low">Baixa</SelectItem>
                <SelectItem value="medium">Media</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="critical">Critica</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterResolved} onValueChange={setFilterResolved}>
              <SelectTrigger className="w-full sm:w-[140px] h-8">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="unresolved">Nao resolvidos</SelectItem>
                <SelectItem value="resolved">Resolvidos</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="h-8">
              <RefreshCw className="h-3 w-3 mr-1" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card className="bg-card/50">
        <CardContent className="p-4">
          <h2 className="font-semibold text-base mb-3">Alertas Recentes</h2>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando alertas...</div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
              <p className="text-muted-foreground mb-3">Erro ao carregar alertas.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-3 w-3 mr-1" />
                Tentar novamente
              </Button>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-muted-foreground">Nenhum alerta encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <SecurityAlertCard key={alert.id} alert={alert} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
